import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, combineLatest, Observable, forkJoin } from 'rxjs';
import { takeUntil, filter as rxFilter, take } from 'rxjs/operators';
import { FILTER_TYPES, IFilterData } from 'src/app/core/feature-modules/table-filter/table-filter.interfaces';
import { SYSTEM_COLORS } from 'src/app/core/feature-modules/whitelabel/style-changer/styles/colors/system-colors.constants';
import {
  IReviewsMetrics,
  IFitersObject,
  PeriodType,
  PeriodText,
} from '../review-management.interfaces';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { ConvertCSVToJSONService } from 'src/app/reports/shared/services/convert-csv-to-json.service';
import { SelectedLocationsStore, SelectedLocationsState } from '../../loc-comps/selected-locations.store';
@Component({
  selector: 'app-metrics-tab',
  templateUrl: './metrics-tab.component.html',
  styleUrls: ['./metrics-tab.component.scss'],
})
export class MetricsTabComponent implements OnInit, OnDestroy {
  showOverview = true;
  reviewsData: IReviewsMetrics;
  dailyAvgRatings: any;
  dailyTotalReviews: any;
  periodType:string;
  SYSTEM_COLORS = SYSTEM_COLORS;
  keyword = '';
  filterData: IFilterData[] = [];
  chipsArray: string[] = [];
  filters: IFitersObject= {};
  public startDate: string;
  public endDate: string;
  public dateFilterDisplayType: 'custom' | 'preset' = 'preset';
  public dateFilterLabel: string = PeriodText.LAST_30_DAYS;
  private readonly customDateRangeRegex = /^\d{4}-\d{2}-\d{2} - \d{4}-\d{2}-\d{2}$/;
  //state: SelectedLocationsState | null = null;
  private selectedDateRange = 'DefaultDate';
  public locationFilter: string = '';
  private ngUnsubscribe$ = new Subject<void>();
  private accountId: number;
  private userId: number;
  private defaultPeriodType: PeriodType = PeriodType.DAY;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly searchDebounceDelayMs = 1500;
  public enableLineGraph: boolean = true;

  constructor(
    private http: HttpClient,
    public sessionService: SessionService,
    private convertCSVToJSONService: ConvertCSVToJSONService,
    private selectedLocationsStore: SelectedLocationsStore
  ) {this.periodType = this.defaultPeriodType}

  /**
     * @description: Get the current account details.
     */
    private getCurrentAccountDetails(): void {
      this.sessionService
        .getSelectedAccount$()
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((account: IAccount) => {
          this.accountId = account?._id;
        });
    }
  
    /**
     * @description: Get the current user details.
     * @returns: void
     * @arguments: void
     */
    private getCurrentUserDetails(): void {
      this.sessionService
        .getCurrentUser$()
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((user) => {
          this.userId = user?.login?._id;
        });
    }

  ngOnInit(): void {
    this.initFilterData();
    this.updateDateFilterDisplay();
    this.initUserFetchData()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([account, user]: [IAccount, any]) => {
        this.accountId = account?._id;
        this.userId = user?.login?._id;
        const getQS = () => {
          const qs = this.filtersQSFromStore();
          return qs && qs.length ? qs : 'operand=AND';
        };

      // Initial load
      this.locationFilter = getQS();
      //this.getInsightsData(this.selectedRange, this.locationFilter);
      // Subsequent loads on any filter change
      this.selectedLocationsStore.state$
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe(() => {
          this.locationFilter = getQS();
          //this.getInsightsData(this.selectedRange, this.locationFilter);
          this.loadReviewsData();
        });
      });

  }

  /**
   * Builds the location filter query string from the persisted selected-locations store.
   * Falls back to the default `operand=AND` when the store has not been initialized yet or
   * has been cleared, and prefers explicit store codes over generic filters when present.
   * @returns A query-string fragment representing the current location filter state.
   */
  private filtersQSFromStore(): string {
    const state = this.selectedLocationsStore.snapshot;
    if (!state) {
      return 'operand=AND';
    }

    const operand = `operand=${state.mode}`;
    if (state.storeCodes.length) {
      return `${operand}&storecode=${state.storeCodes.map(encodeURIComponent).join(',')}`;
    }
    const qs = this.buildQueryFromFiltersRecord(state.filters);
    return qs ? `${operand}&${qs}` : operand;
  }

  private buildQueryFromFiltersRecord(filters: Record<string, string[] | string>): string {
    const parts: string[] = [];
    Object.entries(filters).forEach(([apiKey, raw]) => {
      const qsKey = this.API_TO_QUERY[apiKey] ?? apiKey;
      const vals = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
      if (vals.length) parts.push(`${qsKey}=${encodeURIComponent(vals.join(','))}`);
    });
    return parts.join('&');
  }

  private readonly API_TO_QUERY: Record<string, string> = {
    storecode: 'storecode',
    business_names: 'businessname',
    city: 'city',
    states: 'state',
    countries: 'country',
    tag_list: 'tag',
    label_list: 'labels',
    category_list: 'categories',
  };

  private initUserFetchData(): Observable<[IAccount, any]> {
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();

    return combineLatest([
      this.sessionService.getSelectedAccount$().pipe(rxFilter(Boolean), take(1)) as Observable<IAccount>,
      this.sessionService.getCurrentUser$().pipe(rxFilter(Boolean), take(1)),
    ]).pipe(takeUntil(this.ngUnsubscribe$)) as Observable<[IAccount, any]>;
  }
  

  private initFilterData(): void {
    this.filterData.push(
      /*{
        type: FILTER_TYPES.MULTI,
        title: 'RATINGS',
        options: ['5', '4', '3', '2', '1'],
      },*/
      {
        type: FILTER_TYPES.DATE,
        title: 'DATE',
        options: Object.values(PeriodText),
      },
      /*{
        type: FILTER_TYPES.SINGLE,
        title: 'REVIEW TEXT',
        options: ['Review includes text', 'Review does not include text'],
      }*/
    );
  }

  /**
   * Debounces keyword searches before loading review data.
   * Each keystroke resets the pending request so only the latest input triggers a fetch after the configured delay.
   * @returns void
   */
  userSearch(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.loadReviewsData();
      this.searchDebounceTimer = null;
    }, this.searchDebounceDelayMs);
  }

  onFilterChange(filters: IFitersObject): void {
    this.chipsArray = [].concat.apply([], Object.values(filters));
    this.chipsArray = this.chipsArray.filter((e) => e);

    if (!filters.hasOwnProperty('DATE')) {
      filters = { ...filters, DATE: ['DefaultDate'] };
    } else if (filters.hasOwnProperty('DATE') && !filters['DATE'].length) {
      filters = { ...filters, DATE: ['DefaultDate'] };
    }

    this.filters = filters;
    this.updateDateFilterDisplay(filters?.['DATE']?.[0]);
    this.loadReviewsData();
  }

  /**
   * Returns whether the DATE filter value is a custom ISO range (`YYYY-MM-DD - YYYY-MM-DD`).
   * @param dateFilter Raw DATE filter value from table-filter.
   * @returns True when the value matches the custom date range format.
   */
  private isCustomDateRange(dateFilter: string | undefined): boolean {
    return !!dateFilter && this.customDateRangeRegex.test(dateFilter);
  }

  /**
   * Updates the metrics date header to match the active DATE filter.
   * Custom ranges show `Metrics for` with explicit dates; presets show the filter label only.
   * @param dateFilter Optional DATE filter value; falls back to `this.filters.DATE[0]` when omitted.
   * @returns None.
   */
  private updateDateFilterDisplay(dateFilter?: string): void {
    const activeDateFilter = dateFilter ?? this.filters?.['DATE']?.[0];

    if (this.isCustomDateRange(activeDateFilter)) {
      const [start, end] = activeDateFilter.split(' - ');
      this.dateFilterDisplayType = 'custom';
      this.startDate = start;
      this.endDate = end;
      this.dateFilterLabel = '';
      return;
    }

    this.dateFilterDisplayType = 'preset';
    this.startDate = null;
    this.endDate = null;
    this.dateFilterLabel = this.resolvePresetDateFilterLabel(activeDateFilter);
  }

  /**
   * Maps internal/default DATE filter values to user-facing preset labels.
   * @param dateFilter Raw DATE filter value from table-filter.
   * @returns Preset label shown in the metrics date header.
   */
  private resolvePresetDateFilterLabel(dateFilter: string | undefined): string {
    if (!dateFilter || dateFilter === 'DefaultDate') {
      return PeriodText.LAST_30_DAYS;
    }

    return dateFilter;
  }

  hideOverview(toggle: boolean) {
    this.showOverview = !toggle;
  }

  AddFiltersToReviewData(filters = undefined) {
    const search = this.keyword;
    const dateFilter = filters?.['DATE']?.[0];
    if (!dateFilter) {
      this.periodType = PeriodType.DAY;
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search };
    }
    //Custom date func, waiting for api implementation.
    const dateRangeRegex = /^\d{4}-\d{2}-\d{2} - \d{4}-\d{2}-\d{2}$/;
    if (dateRangeRegex.test(dateFilter)) {
      const [startDate, endDate] = dateFilter.split(' - ');
      const formattedStartDate = startDate.replace(/-/g, '');
      const formattedEndDate = endDate.replace(/-/g, '');
      this.periodType = PeriodType.CUSTOM_DATE;
      return { timeunit: PeriodType.CUSTOM_DATE.toLowerCase(), periods: `${formattedStartDate}_${formattedEndDate}`, search };
    }
  /*if (dateRangeRegex.test(dateFilter)) {
    const [startStr, endStr] = dateFilter.split(' - ');
    const [sy, sm, sd] = startStr.split('-').map(Number);
    const [ey, em, ed] = endStr.split('-').map(Number);
    const startDate = new Date(sy, sm - 1, sd);
    const endDate = new Date(ey, em - 1, ed);
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff > 60) {
      const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12
        + (endDate.getMonth() - startDate.getMonth());
      this.periodType = PeriodType.MONTH;
      return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: String(monthsDiff) };
    }

    this.periodType = PeriodType.DAY;
    return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysDiff) };
  }*/

  switch (dateFilter) {
    case PeriodText.THIS_MONTH: {
      this.enableLineGraph = true;
      const today = new Date();
      const daysPassedThisMonth = today.getDate();
      this.periodType = PeriodType.DAY;
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisMonth-1), search };
    }

    case PeriodText.LAST_30_DAYS: {
      this.periodType = PeriodType.DAY;
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search };
    }

    case PeriodText.LAST_MONTH: {
      const today = new Date();
      const daysPassedThisMonth = today.getDate();
      this.periodType = PeriodType.DAY;
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisMonth+31-1), search };
    }

    case PeriodText.YEAR_TO_DATE: {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const daysPassedThisYear = Math.ceil((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));

      if (daysPassedThisYear > 60) {
        const monthsPassed = today.getMonth(); 
        this.periodType = PeriodType.MONTH;
        return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: String(monthsPassed), search };
      }

      this.periodType = PeriodType.DAY;
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisYear), search };
    }

    case PeriodText.LAST_12_MONTHS: {
      this.periodType = PeriodType.MONTH;
      return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: '12', search };
    }

    case PeriodText.ALL_TIME: {
      this.periodType = PeriodType.YEAR;
      return { timeunit: (PeriodType.ALL_TIME).toLowerCase(), periods: '0', search };
    }

    default:
      return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search };
  }
}

  loadReviewsData(): void {
    const {timeunit,periods,search} = this.AddFiltersToReviewData(this.filters);
    const config = {
      header: true,
    };
    forkJoin({
      metrics:this.getExportedReport(this.accountId, this.userId, 'reviews/account', timeunit, periods, search, this.locationFilter),
    }).pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ metrics}) => {
          //Parsing reviews:
          const currDataReviews = this.convertCSVToJSONService
            .csvToJson(metrics, config)
            ['data'].filter((data) => Object.keys(data).length > 1);
          //Directories are considered search engines in this case, currently we are not supporting rows that do not contain "all"  
          //const directoryRows = currDataReviews.filter(row => row.directory !== 'ALL');
          let allRows = currDataReviews.filter(row => row.directory == 'ALL');
          //Edge case for last month since we have to filter through the records for the ones of last month only.
          if (this.filters?.["DATE"]?.[0] == PeriodText.LAST_MONTH) {
            const today = new Date();
            const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            allRows = allRows.filter(row => {
              const [year, month, day] = row.period_start_date.split('-').map(Number);
              const date = new Date(year, month - 1, day);
              return date >= firstDayLastMonth && date <= lastDayLastMonth;
            });
          }
          const filteredRows = allRows;
          const { totalRatingsDaily, totalReviewsDaily, highStarsCount, medStarsCount, lowStarsCount, totalRepliedTo, avgRowRating, avgRowReviews } = filteredRows.reduce((prev, curr) => {
            return {
              totalRatingsDaily: prev.totalRatingsDaily + parseFloat(curr.Average_rating),
              totalReviewsDaily: prev.totalReviewsDaily + parseFloat(curr.Total_ratings),
              highStarsCount: prev.highStarsCount + parseFloat(curr.Total_positive_ratings),
              medStarsCount: prev.medStarsCount + parseFloat(curr.Total_neutral_ratings),
              lowStarsCount: prev.lowStarsCount + parseFloat(curr.Total_negative_ratings),
              totalRepliedTo: prev.totalRepliedTo + parseFloat(curr.response_count),
              avgRowRating: [...prev.avgRowRating, { date: curr.period_start_date, value: curr.Average_rating }],
              avgRowReviews: [...prev.avgRowReviews, { date: curr.period_start_date, value: curr.Total_ratings }],
            };
          }, { totalRatingsDaily: 0, totalReviewsDaily: 0, highStarsCount: 0, medStarsCount: 0, lowStarsCount: 0, totalRepliedTo:0, avgRowRating:[], avgRowReviews:[] });
          
          if(filteredRows.length > 1) {
            this.enableLineGraph = true;
          }else{
            this.enableLineGraph = false;
          }

          const reviewData: IReviewsMetrics = {            
              account: filteredRows[0]?.account_name,
              account_id: filteredRows[0]?.account_ident,
              dateStart: filteredRows[0]?.period_start_date,
              dateEnd: undefined,
              avgStarRating: Number.isNaN((totalRatingsDaily/filteredRows.length)) ? 0 : (totalRatingsDaily/filteredRows.length),
              highStarsCount: Number.isNaN(highStarsCount) ? 0 : highStarsCount,
              medStarsCount: Number.isNaN(medStarsCount) ? 0 : medStarsCount,
              lowStarsCount: Number.isNaN(lowStarsCount) ? 0 : lowStarsCount,
              totalReviews: Number.isNaN(totalReviewsDaily) ? 0 : totalReviewsDaily,
              totalRepliedTo: Number.isNaN(totalRepliedTo) ? 0 : totalRepliedTo,
              avgRatingsDaily: [],   
              totalReviewsDaily: [],             
          }      
          this.reviewsData = reviewData;          
          this.dailyAvgRatings = avgRowRating;
          this.dailyTotalReviews = avgRowReviews;
        },
        (err)=>{
          console.log(err);
        }
      );
    
  }
  /**
   * Builds the report query string while avoiding duplicate `storecode` params.
   * Existing location filters remain authoritative, and the search term is only applied as `storecode` when the filters do not already define one.
   * @param timeunit The report aggregation unit expected by the insights API.
   * @param periods The selected period value sent to the insights API.
   * @param filters Serialized global filter query string, if any.
   * @param search Optional search-box value that maps to the `storecode` query param.
   * @returns The normalized report query string.
   */
  private buildReportQueryString(timeunit: string, periods: any, filters?: string, search?: string): string {
    const queryParams = new URLSearchParams({
      timeunit,
      periods: String(periods),
    });

    if (filters && filters.length) {
      const filterParams = new URLSearchParams(filters);
      filterParams.forEach((value, key) => queryParams.append(key, value));
    }

    const trimmedSearch = search?.trim();
    if (trimmedSearch && !queryParams.has('storecode')) {
      queryParams.set('storecode', trimmedSearch);
    }

    return queryParams.toString();
  }

  //This function is duplicated as instructed due to the amount of repetitions of similar snippets in this project, 
  //it will be replaced in console 4.0
  /**
   * Fetches the metrics export report for the current account and user.
   * The request always includes the selected time range, preserves serialized location filters, and only adds the search-based `storecode` when the filters do not already supply one.
   * @param accountId The current account identifier included in the JWT payload.
   * @param userId The current user identifier included in the JWT payload.
   * @param directoryName The report directory path segment under the insights API.
   * @param timeunit The report aggregation unit expected by the insights API.
   * @param periods The selected period value sent to the insights API.
   * @param search Optional search-box value that maps to the `storecode` query param when no filter store code is present.
   * @param filters Optional serialized location filter query string appended to the request.
   * @returns An observable that emits the CSV report response as plain text.
   */
  public getExportedReport(
      accountId: any,
      userId: any,
      directoryName: string,
      timeunit: string,
      periods: any,
      search?: string,
      filters?: string
    ): Observable<any> { 
      const payloadObj = {
        iss: 'https://platform.renderseo.com', 
        sub: userId, 
        account_id: accountId,
        iat: new Date().getTime(),
        exp: new Date().getTime() + 3 * 6000,
      };
      const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
      const options = {
        headers: new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `${jwtToken}`,
        }),
        responseType: 'text' as any,
      };
  
      const queryString = this.buildReportQueryString(timeunit, periods, filters, search);
      const url = `https://io.renderseo.com/${directoryName}?${queryString}`;
      console.log(url);
      return this.http.get(url, options);
    }

  /*private buildWhereClause(): Array<string> {
    const filterClauses: string[][] = [];
    const tempFilterArr: string[] = [];
    const isFiltersApplied = this.chipsArray.length > 0;

    const pushQueriesToMainArray = () => {
      const filterClause: Array<string> = ['-or', ...tempFilterArr];
      filterClauses.push(filterClause);
      tempFilterArr.length = 0;
    };

    if (isFiltersApplied) {
      for (const [key, value] of Object.entries(this.filters)) {
        if (value?.length > 0 && value[0] != null) {
          switch (key) {
            case 'TAGS':
              value.forEach((tag: string) => tempFilterArr.push(`tags @> '{"${tag}"}'`));
              pushQueriesToMainArray();
              break;
            case 'REVIEWS':
              value.forEach((element: string) => {
                element === 'Responded to'
                  ? tempFilterArr.push('NOT replyComment IS NULL')
                  : tempFilterArr.push('replyComment IS NULL');
              });
              pushQueriesToMainArray();
              break;
            case 'RATINGS':
              value.forEach((element: string) => {
                const rating = parseInt(element, 10);
                if (rating >= 1 && rating <= 5) {
                  tempFilterArr.push(`starRating = ${rating}`);
                }
              });
              pushQueriesToMainArray();
              break;
            case 'DATE':
              value.forEach((element: string) => {
                this.selectedDateRange = element;
                switch (element) {
                  case 'This month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Last 30 days':
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  case 'Last month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) - 1 and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Year to date':
                    tempFilterArr.push(`extract(year from r.date) = extract(year from current_date)`);
                    break;
                  case 'Last 12 months':
                    tempFilterArr.push(`r.date > (now() - interval '12 month')`);
                    break;
                  case 'DefaultDate':
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  default:
                    const dateArray: string[] = element.split(' - ');
                    if (dateArray.length === 2) {
                      tempFilterArr.push(
                        `reviewCreatedAt >= '${dateArray[0]}' AND reviewCreatedAt <= '${dateArray[1]}'`
                      );
                    }
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
            case 'REVIEW TEXT':
              value.forEach((element: string) => {
                element === 'Review includes text'
                  ? tempFilterArr.push('reviewercomment IS NOT NULL')
                  : tempFilterArr.push('reviewercomment IS NULL');
              });
              pushQueriesToMainArray();
              break;
          }
        }
      }
    } else {
      this.selectedDateRange = 'DefaultDate';
      tempFilterArr.push(`r.date > (now() - interval '30 day')`);
      pushQueriesToMainArray();
    }

    if (this.keyword) {
      tempFilterArr.push(`storeCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`businessName::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`tags::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`addressLines::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`city::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`state::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`postalCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`reviewerComment::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`replyComment::text ~* '\\y${this.keyword}'`);
      pushQueriesToMainArray();
    }

    const whereClauseArray: Array<any> = [
      '-and',
      "r._status IN ('A', 'UA', 'UP')",
      "vendorIdent='gmb'",
      ...filterClauses,
    ];
    return whereClauseArray;
  }*/

  /**
   * Releases component subscriptions and any pending search debounce timer.
   * Prevents delayed search requests from firing after the component has been destroyed.
   * @returns void
   */
  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}